import { Component, OnInit, Signal, computed, input, signal, viewChild } from '@angular/core';
import { dispatch, select } from '@store/injectables';
import { Pmo } from '@scenario/models/pmo.model';
import {
  PmoViewModel,
  createPmo,
  retrieveReleases,
  selectPmoViewModel,
  updatePmo,
} from '@store/scenario/pmo/pmo-slice';
import { retrieveRegions, selectRegionsViewModel } from '@store/scenario/regions/regions-slice';
import { retrieveSources, selectSourcesViewModel } from '@store/scenario/sources/sources-slice';
import {
  ButtonComponent,
  DropdownComponent,
  FormFieldComponent,
  FormFieldItemDirective,
  FormFieldLabelDirective,
  IconComponent,
  ModalActionsDirective,
  ModalComponent,
  ModalTitleDirective,
  TagComponent,
} from '@totalenergiescode/tds-angular';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'app-pmo-form-modal',
  templateUrl: './pmo-form-modal.component.html',
  styleUrl: './pmo-form-modal.component.scss',
  imports: [
    ButtonComponent,
    DropdownComponent,
    FormFieldComponent,
    FormFieldItemDirective,
    FormFieldLabelDirective,
    IconComponent,
    ModalActionsDirective,
    ModalComponent,
    ModalTitleDirective,
    TagComponent,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
  ],
})
export class PmoFormModalComponent implements OnInit {
  private readonly dispatch = dispatch();
  public readonly modal = viewChild.required(ModalComponent);

  public readonly pmo = input<Pmo | undefined>(undefined);

  public readonly regionsViewModel = select(selectRegionsViewModel);
  public readonly sourcesViewModel = select(selectSourcesViewModel);
  public readonly pmoViewModel: Signal<PmoViewModel> = select(selectPmoViewModel);

  public readonly selectedFile = signal<File | null>(null);
  public readonly currentFileUrl = signal('');
  public readonly currentOriginalFileName = signal('');
  public readonly selectedZoneIds = signal<number[]>([]);
  public readonly selectedSourceIds = signal<number[]>([]);
  public readonly selectedReleases = signal<string[]>([]);
  public readonly newRelease = signal('');
  public readonly fileError = signal(false);

  public readonly isEdit = computed(() => (this.pmo()?.id ?? 0) > 0);

  public readonly fileName = computed(() => this.selectedFile()?.name ?? this.currentOriginalFileName());

  public readonly releaseOptions = computed(() =>
    this.pmoViewModel().releases
      .filter(release => !this.selectedReleases().includes(release))
      .map(release => ({ value: release, label: release })),
  );

  ngOnInit(): void {
    this.dispatch(retrieveSources());
    this.dispatch(retrieveRegions());
    this.dispatch(retrieveReleases());

    const pmo = this.pmo();
    if (pmo) {
      this.currentFileUrl.set(pmo.fileUrl);
      this.currentOriginalFileName.set(pmo.originalFileName);
      this.selectedZoneIds.set(pmo.zones.map(zone => zone.id));
      this.selectedSourceIds.set(pmo.sources.map(source => source.id));
      this.selectedReleases.set([...pmo.sourcePublicationDates]);
    }
  }

  onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.selectedFile.set(inputElement.files?.[0] ?? null);
    this.fileError.set(false);
  }

  onZonesChange(zoneIds: number[]): void {
    this.selectedZoneIds.set(zoneIds);
  }

  onSourcesChange(sourceIds: number[]): void {
    this.selectedSourceIds.set(sourceIds);
  }

  onReleasePicked(release: string): void {
    this.addRelease(release);
  }

  onNewReleaseChange(event: Event): void {
    this.newRelease.set((event.target as HTMLInputElement).value);
  }

  addNewRelease(): void {
    this.addRelease(this.newRelease());
    this.newRelease.set('');
  }

  addRelease(release: string): void {
    const trimmed = release.trim();
    if (trimmed.length > 0 && !this.selectedReleases().includes(trimmed)) {
      this.selectedReleases.update(releases => [...releases, trimmed]);
    }
  }

  removeRelease(release: string): void {
    this.selectedReleases.update(releases => releases.filter(value => value !== release));
  }

  save(): void {
    if (!this.isEdit() && !this.selectedFile()) {
      this.fileError.set(true);
      return;
    }

    const zones = this.regionsViewModel().regions.filter(zone => this.selectedZoneIds().includes(zone.id));
    const sources = this.sourcesViewModel().sources.filter(source => this.selectedSourceIds().includes(source.id));
    const pmo: Pmo = {
      fileUrl: this.currentFileUrl(),
      originalFileName: this.currentOriginalFileName(),
      zones,
      sources,
      sourcePublicationDates: this.selectedReleases(),
    };
    const file = this.selectedFile() ?? undefined;
    const existing = this.pmo();
    if (existing?.id) {
      this.dispatch(updatePmo({ pmo: { ...pmo, id: existing.id }, file }));
    } else {
      this.dispatch(createPmo({ pmo, file }));
    }
    this.modal().closeModal('saved');
  }

  close(): void {
    this.modal().closeModal('cancel');
  }


}

